import React from 'react';
import {Table} from '@/web-components';
import {useIntl} from 'react-intl';
import {ProjectPortfolioReportTooltips} from '../ProjectPortfolioReportTooltips';
import {BUDGET_TYPE} from '../../../../constants';
import {
	BudgetTypeColumn,
	renderClient,
	renderDate,
	renderOwners,
	renderRateCard,
	renderStage,
	renderStatus,
	getProjectData,
	getPhaseData,
	renderPriorityLevel,
	renderDescription,
} from '../period/ProjectPortfolioPeriodRow';
import * as budgetUtils from '../../../new-ui/project/project-budget-v3/util/BudgetUtils';
import moment from 'moment';
import {CurrencyColumn} from '../CurrencyColumn';
import {PercentageColumn} from '../PercentageColumn';
import {OpportunityTooltip} from '../OpportunityTooltip';
import {ACTUALS_AND_FORECAST_TO_COMPLETE, WIN_PROBABILITY_ADJUSTED_VALUES} from '../ProjectPortfolioSingleNumberOptions';
import {isProjectOpportunity} from '../../../../forecast-app/shared/util/projectStageUtil';

const ProjectPortfolioMonthByMonthRow = ({
	rowData,
	tableColumnProps,
	headerColumn,
	canExpand,
	hasBaseline,
	selectedSingleValue,
	fixedPriceLocks,
	useWinProbability,
}) => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const projectData = getProjectData(rowData);
	const phaseData = getPhaseData(rowData);
	const hasLowerWinChance =
		hasBaseline &&
		rowData.project?.useBaseline &&
		isProjectOpportunity(rowData.project) &&
		rowData.project?.baselineWinChance < 1;
	const lowerWinChanceTooltip =
		selectedSingleValue === 'billableTotalTimeAndExpensesAtCompletion'
			? ProjectPortfolioReportTooltips.BillableTotalTimeAndExpensesAtCompletionLowerWinChanceTooltip(formatMessage)
			: selectedSingleValue === 'totalCostAtCompletion'
			? ProjectPortfolioReportTooltips.TotalCostAtCompletionLowerWinChanceTooltip(formatMessage)
			: [];
	const showWinChanceTooltip = hasLowerWinChance && lowerWinChanceTooltip.length;

	const isOpportunityProject = isProjectOpportunity(projectData);
	const hideOpportunityProjectFields = isOpportunityProject && ACTUALS_AND_FORECAST_TO_COMPLETE.includes(selectedSingleValue);
	const winProbabilityAppliedToProject = isOpportunityProject && useWinProbability;
	const winProbabilityAppliedToValues =
		winProbabilityAppliedToProject && WIN_PROBABILITY_ADJUSTED_VALUES.includes(selectedSingleValue);

	const monthsData = [];
	const totalData = [];
	const timeData = [];
	const expenseData = [];

	for (const period of Object.values(rowData.aggregatedFinancialNumbers)) {
		const periodStartDate = period.split(',')[0];
		let startMonth = parseInt(periodStartDate.split('-')[1]);
		const periodEndDate = period.split(',')[1];
		const endMonth = parseInt(periodEndDate.split('-')[1]);
		const isColumnLocked =
			selectedSingleValue === 'recognitionAmount' && fixedPriceLocks
				? budgetUtils.isColumnLocked(fixedPriceLocks, {
						startDate: moment(periodStartDate),
						endDate: moment(periodEndDate),
				  })
				: false;
		while (startMonth <= endMonth) {
			const monthAmount = parseFloat(period.split(',')[2]);
			totalData.push({monthAmount, isColumnLocked});
			startMonth += 1;
		}
	}

	for (const period of Object.values(rowData.timeAggregatedFinancialNumbers)) {
		const periodStartDate = period.split(',')[0];
		let startMonth = parseInt(periodStartDate.split('-')[1]);
		const periodEndDate = period.split(',')[1];
		const endMonth = parseInt(periodEndDate.split('-')[1]);
		const isColumnLocked =
			selectedSingleValue === 'recognitionAmount' && fixedPriceLocks
				? budgetUtils.isColumnLocked(fixedPriceLocks, {
						startDate: moment(periodStartDate),
						endDate: moment(periodEndDate),
				  })
				: false;
		while (startMonth <= endMonth) {
			const monthAmount = parseFloat(period.split(',')[2]);
			timeData.push({monthAmount, isColumnLocked});
			startMonth += 1;
		}
	}

	for (const period of Object.values(rowData.expenseAggregatedFinancialNumbers)) {
		const periodStartDate = period.split(',')[0];
		let startMonth = parseInt(periodStartDate.split('-')[1]);
		const periodEndDate = period.split(',')[1];
		const endMonth = parseInt(periodEndDate.split('-')[1]);
		const isColumnLocked =
			selectedSingleValue === 'recognitionAmount' && fixedPriceLocks
				? budgetUtils.isColumnLocked(fixedPriceLocks, {
						startDate: moment(periodStartDate),
						endDate: moment(periodEndDate),
				  })
				: false;
		while (startMonth <= endMonth) {
			const monthAmount = parseFloat(period.split(',')[2]);
			expenseData.push({monthAmount, isColumnLocked});
			startMonth += 1;
		}
	}

	for (let i = 0; i < totalData.length; i++) {
		monthsData.push(expenseData[i]);
		monthsData.push(timeData[i]);
		monthsData.push(totalData[i]);
	}

	const isFixedPriceProject = () => {
		return [BUDGET_TYPE.FIXED_PRICE, BUDGET_TYPE.FIXED_PRICE_V2].includes(projectData.budgetType);
	};

	if (canExpand !== undefined) {
		tableColumnProps.canExpand = canExpand;
	}

	return (
		<Table.Row {...tableColumnProps}>
			<Table.Column>{headerColumn}</Table.Column>
			{/* Project Overview */}
			<Table.Column>{renderPriorityLevel(projectData.priorityLevel)}</Table.Column>
			<PercentageColumn value={projectData.baselineWinChance} />
			<Table.Column>{renderDate(phaseData.startDate ?? projectData.startDate)}</Table.Column>
			<Table.Column>{renderDate(phaseData.endDate ?? projectData.endDate)}</Table.Column>
			<Table.Column>{renderClient(projectData.client)}</Table.Column>
			<Table.Column>{renderStatus(projectData.currentProjectStatus)}</Table.Column>
			<Table.Column>{renderDescription(projectData.currentProjectStatus)}</Table.Column>
			<Table.Column>{renderOwners(projectData.projectPersons)}</Table.Column>
			<Table.Column>{renderStage(projectData.stage)}</Table.Column>
			<BudgetTypeColumn projectData={projectData} />
			<Table.Column>{renderRateCard(projectData.rateCard)}</Table.Column>
			<PercentageColumn value={projectData.completion / 100} />
			<CurrencyColumn
				value={isFixedPriceProject() ? projectData.budgetBaseCurrency : null}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={winProbabilityAppliedToProject}
				tooltip={
					winProbabilityAppliedToProject && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)
				}
			/>
			{/* Month by month financials */}
			{monthsData.map(monthData => (
				<Table.Column greyedOut={monthData.isColumnLocked}>
					{hideOpportunityProjectFields ? (
						<OpportunityTooltip />
					) : (
						<CurrencyColumn
							value={monthData.monthAmount}
							showGenericWinProbabilityTooltip={winProbabilityAppliedToValues}
							tooltip={
								winProbabilityAppliedToValues &&
								ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)
							}
							hasLowerWinChance={showWinChanceTooltip}
							lowerWinChanceTooltip={lowerWinChanceTooltip}
							greyedOut={monthData.isColumnLocked}
							currency={rowData.currency}
							usePadding={false}
						/>
					)}
				</Table.Column>
			))}
		</Table.Row>
	);
};

export default ProjectPortfolioMonthByMonthRow;
